<template>
  <div>
    <Message
      v-if="isBulkCampaign"
      severity="warn"
      :closable="false"
      v-html="t('campaigns.bulk.messages.alertFiltersMarch2025')"
      style="padding: 1rem 1.5rem;"
    />
    <FilterGlobalInformations
      v-model="commonFields"
      :form-validation="formValidationErrors"
      :is-edit="isEdit"
    />

    <div class="field-group pt-2">
      <div class="field-group-content">
        <div class="field-wrapper">
          <ButtonGroup
            v-model="relevantPeriod"
            :options="periods"
            title="automatedScenarios.fields.relevantPeriod"
          />
          <FieldErrors
            :key="componentFieldErrorsKey"
            :errors="formValidation"
            field="period_concerned"
          />
        </div>
      </div>
    </div>

    <div
      v-if="relevantPeriod === '1'"
      class="field-group pt-2"
    >
      <div>
        <div class="field-group-content">
          <div class="field-wrapper">
            <PeriodSelector
              v-model="period"
              :units="units"
              title="automatedScenarios.fields.duration_2"
            />
            <FieldErrors
              :key="componentFieldErrorsKey"
              :errors="formValidation"
              field="period_time"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <FilterPanelContent
    :active-items="activeItemsFilterPanel"
    :panels="['segments']"
  >
    <template #segments>
      <FilterSegmentForm
        :segments="segmentsForForm"
        :active-segments="activeSegments"
        :items="filters"
        :scenario-type="campaignType"
        :current-step-id="operatorId"
        @add:segment="handleSaveSegment($event)"
        @remove:segment="handleRemoveSegment($event.segmentName)"
      />
      <div
        v-if="hasActiveSegments"
        class="field-wrapper mt-3"
      >
        <ButtonGroup
          v-model="segmentOperator"
          :options="logicalOperators"
          title="automatedScenarios.fields.visitorMustValidate"
        />
        <FieldErrors
          :key="componentFieldErrorsKey"
          :errors="formValidation"
          field="operatorBoxNav"
        />
      </div>
    </template>
  </FilterPanelContent>
  <FieldErrors
    :key="componentFieldErrorsKey"
    :errors="formValidation"
    field="products"
  />
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeMount,
  PropType,
  Ref,
  ref,
  SetupContext,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Message from 'primevue/message';
import { asInt } from '@/helpers';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import type { FilterField, FilterSegment, Period } from '@/types/flow-types';
import getSegments from '@/components/flows/operators/filters/NavigationHistory/segments';
import {
  ButtonGroup,
  FilterGlobalInformations,
  FilterPanelContent,
  FilterSegmentForm,
  PeriodSelector,
} from '@/components/flows/operators/shared';
import { TypeCampaignEnum } from '@/types';
import type { NavigationHistoryData, NavigationHistoryFilters } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'FilterNavigationHistoryForm',

  components: {
    FieldErrors,
    FilterGlobalInformations,
    ButtonGroup,
    PeriodSelector,
    FilterPanelContent,
    FilterSegmentForm,
    Message,
  },

  props: {
    modelValue: {
      type: Object as PropType<NavigationHistoryData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },

    campaignType: {
      type: String as PropType<TypeCampaignEnum>,
      default: TypeCampaignEnum.AUTOMATION,
    },

    operatorId: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: NavigationHistoryData; formValidation: any; campaignType: TypeCampaignEnum; operatorId: string | null }, context: SetupContext) {
    const { t } = useI18n();
    const componentFieldErrorsKey = ref(0);
    const formValidationErrors = ref(props.formValidation);

    const isBulkCampaign = computed(() => props.campaignType === TypeCampaignEnum.BULK);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const isEdit = ref(!!props.modelValue.custom_title);

    const commonFields = ref({
      custom_title: props.modelValue.custom_title,
      custom_description: props.modelValue.custom_description,
      outputs_number: asInt(props.modelValue.outputs_number),
      initial_number_outputs: props.modelValue.initial_number_outputs,
    });

    const relevantPeriod = ref(props.modelValue.period_concerned);
    const segmentOperator = ref(props.modelValue.operatorBoxNav);
    const period = ref<Period>({
      unit: props.modelValue.period_time?.unit ?? 'MINUTE',
      value: props.modelValue.period_time?.value ?? 1,
    });

    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
    ];
    const periods = [
      { value: '0', label: t('automatedScenarios.fields.periods.lastVisit') },
      { value: '1', label: t('automatedScenarios.fields.periods.definedPeriod') },
    ];
    const logicalOperators = [
      { value: 'and', label: t('automatedScenarios.fields.allSegments') },
      { value: 'or', label: t('automatedScenarios.fields.atLeastOne') },
    ];

    const segments: Ref<FilterSegment<NavigationHistoryFilters>[]> = ref([]);
    const segmentsForForm = computed(() => segments.value as unknown as FilterSegment<unknown>[]);
    const activeSegments = computed(() => {
      if (segments.value.length === 0) return [];
      const active: { name: string; label: string; show: boolean }[] = [];
      const findSegmentLabel = (id: string) => {
        const seg = segments.value.find((segment) => segment.id === id);
        return seg?.option.label ?? id;
      };
      if (Object.keys(props.modelValue.products).length > 0) {
        active.push({
          name: 'products',
          label: findSegmentLabel('products'),
          show: true,
        });
      }
      if (Object.keys(props.modelValue.categories).length > 0) {
        active.push({
          name: 'categories',
          label: findSegmentLabel('categories'),
          show: true,
        });
      }
      if (Object.keys(props.modelValue.manufacturers).length > 0) {
        active.push({
          name: 'manufacturers',
          label: findSegmentLabel('manufacturers'),
          show: true,
        });
      }
      if (Object.keys(props.modelValue.urls).length > 0) {
        active.push({
          name: 'urls',
          label: findSegmentLabel('urls'),
          show: true,
        });
      }
      return active;
    });

    const activeItemsFilterPanel = computed(() => ({ segments: activeSegments.value.length }));
    const hasActiveSegments = computed(() => activeSegments.value.length > 1);

    const filters: Ref<NavigationHistoryFilters> = ref({
      products: props.modelValue.products,
      categories: props.modelValue.categories,
      manufacturers: props.modelValue.manufacturers,
      urls: props.modelValue.urls,
    });

    onBeforeMount(async () => {
      segments.value = await getSegments();
      if (props.campaignType === TypeCampaignEnum.BULK) {
        relevantPeriod.value = '1';
      }
    });

    const setPickerSegment = (segment: FilterSegment<NavigationHistoryFilters>) => {
      const number = segment.fields[0];
      const operator = segment.fields[1];
      const picker = segment.fields[2];
      const value: Record<string, unknown> = {
        [number.id]: number.data,
        [operator.id]: operator.data,
      };
      if (picker.data && picker.data.selection.selected.length > 0) {
        value[picker.id] = picker.data;
      }
      value.litteral_title = segment.fields
        .reduce((acc: Record<string, string>, field: FilterField) => ({
          ...acc,
          [field.id]: field.litteral_title,
        }), {});
      return value;
    };

    const setUrlSegment = (segment: FilterSegment<NavigationHistoryFilters>) => ({
      lists: segment.fields[0].data.map((url: string) => ({ url })),
      operator: segment.fields[1].data,
      litteral_title: {
        [segment.fields[0].id]: segment.fields[0].litteral_title,
      },
    });

    const update = () => {
      context.emit('update:modelValue', createDefaultData({
        ...commonFields.value,
        products: filters.value.products ?? {},
        categories: filters.value.categories ?? {},
        manufacturers: filters.value.manufacturers ?? {},
        urls: filters.value.urls ?? {},
        operatorBoxNav: segmentOperator.value,
        period_concerned: relevantPeriod.value,
        period_time: period.value,
      }));
    };

    const handleSaveSegment = (segment: FilterSegment<NavigationHistoryFilters>) => {
      if (['products', 'categories', 'manufacturers'].includes(segment.id)) {
        filters.value[segment.id] = setPickerSegment(segment);
      } else {
        filters.value[segment.id] = setUrlSegment(segment);
      }
      update();
    };

    const handleRemoveSegment = (segmentName: keyof NavigationHistoryFilters) => {
      filters.value[segmentName] = {};
      update();
    };

    watch([commonFields, relevantPeriod, period, segments, segmentOperator], update, { deep: true });

    return {
      t,
      componentFieldErrorsKey,
      formValidationErrors,
      isBulkCampaign,
      isEdit,
      commonFields,
      relevantPeriod,
      period,
      units,
      periods,
      logicalOperators,
      segmentOperator,
      segments,
      segmentsForForm,
      activeSegments,
      activeItemsFilterPanel,
      hasActiveSegments,
      filters,
      handleSaveSegment,
      handleRemoveSegment,
    };
  },
});
</script>

<style lang="scss" scoped>
.field-group {
  margin-bottom: 1rem;
}
</style>
